 
import { Exceptions_ErrorModel } from '@monorepo-types/dc';

import type { ApiResult } from './types';

/**
 * This class wraps the errors from DC
 * DC will expose its errors in the body via the 'errors' prop
 * That errors prop is an array of errors shaped like:
 * instanceId: string; // guid for the individual occurance - useful for backtracking in dc logs nothing more
 * code?: string; // a unique identifer prefixed with a namespace seperated with a pipe (|)
 * details?: string; // debugging information - not available in production
 * type?: string; // The human readable error type (for instance DataNotFoundExeception) - useful in case client code wants to address certain issues with a specific UX
 * innerErrors?: Array<Exceptions_ErrorModel>; // The potential underlying errors to this error - mostly to implementation detailed for FE to use
 *
 * In the near future this model will be extended with front end friendly error messages
 */

export class ApiError extends Error {
  public readonly url: URL;
  public readonly status: number;
  public readonly statusText: string;
  public readonly body: any;
  public readonly headers: Headers;

  public readonly hasErrors: boolean;
  public readonly errors?: Exceptions_ErrorModel[];
  public readonly firstError?: Exceptions_ErrorModel;

  constructor(response: ApiResult, message: string) {
    super(message);

    this.url = response.url;
    this.status = response.status;
    this.statusText = response.statusText;
    this.body = response.body;
    this.headers = response.headers;

    try {
      this.errors = this.body.errors as Exceptions_ErrorModel[];
      this.firstError = this.errors?.[0];
      this.hasErrors = true;
    } catch {
      this.hasErrors = false;
    }
  }

  /**
   * Iteratively traverses the error tree to find the deepest error node.
   * @returns The deepest Exceptions_ErrorModel found, or null if none exist.
   */
  getDeepestError(): Exceptions_ErrorModel | null {
    let deepestError: Exceptions_ErrorModel | null = null;
    let maxDepth = -1;

    const stack: Array<{ error: Exceptions_ErrorModel; depth: number }> | undefined = this.errors?.map(error => ({
      error,
      depth: 0,
    }));

    while (stack && stack.length > 0) {
      const { error, depth } = stack.pop()!;
      if (depth > maxDepth) {
        deepestError = error;
        maxDepth = depth;
      }
      if (Array.isArray(error.innerErrors) && error.innerErrors.length > 0) {
        error.innerErrors.forEach(inner => {
          stack.push({ error: inner, depth: depth + 1 });
        });
      }
    }

    return deepestError;
  }
}
